import type { DealLine } from '../../types/domain';
import {
  contractMonthStart,
  contractPeriodEnd,
  contractTotalMonths,
  dateFromIso,
  isoStr,
  workingDaysInPeriod,
} from '../dates';
import { toNumber } from '../numbers';
import { estimatedOpenMonths } from './contractLength';

/** Jours ouvrés d'un mois de contrat, rattachés à l'année où ce mois commence. */
export interface MonthDays {
  year: number;
  days: number;
}

// Régie (arbitrage du 2026-10-02) : le vendu se compte en jours ouvrés réels, comme la capacité
// de la phase, et non plus 20 jours par mois. Jours ouvrés de chacun des `months` mois du contrat
// (mois anniversaires), le dernier coupé à la date de fin si elle tombe avant.
export function contractWorkingDays(
  startIso: string,
  months: number,
  endIso?: string | null,
): MonthDays[] {
  const start = dateFromIso(startIso);
  const out: MonthDays[] = [];
  for (let k = 0; k < months; k++) {
    const from = isoStr(contractMonthStart(start, k));
    const periodEnd = isoStr(contractPeriodEnd(start, k + 1));
    const to = endIso && endIso < periodEnd ? endIso : periodEnd;
    out.push({ year: Number(from.slice(0, 4)), days: workingDaysInPeriod(from, to) });
  }
  return out;
}

/* Valeur d'une régie : devs × TJM × jours ouvrés réels, sur toute la durée ou sur une année. */
export function regieValue(
  l: DealLine,
  devs: unknown,
  tjm: unknown,
  openMonths: number,
  year: number | string = 'all',
): number {
  if (!l.date_debut_prevue) return 0;
  const months = contractTotalMonths(l.date_debut_prevue, l.date_fin_prevue) || openMonths;
  const days = contractWorkingDays(l.date_debut_prevue, months, l.date_fin_prevue)
    .filter((m) => year === 'all' || m.year === Number(year))
    .reduce((s, m) => s + m.days, 0);
  return (toNumber(devs) || 0) * (toNumber(tjm) || 0) * days;
}

/* Potentiel d'une régie datée : devs × TJM estimés (sinon vendus) × jours ouvrés réels. */
export function regieEstimated(l: DealLine, year: number | string = 'all'): number {
  const estimated = l.nb_devs_estime != null && l.nb_devs_estime !== '';
  const devs = estimated ? l.nb_devs_estime : l.nb_devs_vendu;
  const tjm = estimated ? l.tjm : l.tjm_vendu;
  return regieValue(l, devs, tjm, estimatedOpenMonths(l), year);
}
